import router from "@/router";
import { ROUTE_NAMES, ROUTE_PATHS } from "@/router/types";
import { ApiEndpoints } from "@/static/api-endpoints";
import { useMainStore } from "@/stores/main";
import axios from "axios";
import { throttle } from "lodash";
import { useProposalStore } from "@/stores/risk/risk";
import { computed } from "vue";

const inactivityPeriodMinutes = 20;
const warningPeriodMinutes = 18;
const refreshPeriodMinutes = 5;

const inactivityPeriodMilliseconds = inactivityPeriodMinutes * 60000;
const warningPeriodMilliseconds = warningPeriodMinutes * 60000;
const refreshPeriodMilliseconds = refreshPeriodMinutes * 60000;
const selectedQuote = computed(() => useProposalStore().proposal.quote);

const SESSION_STORAGE_KEY = "lastSessionRefresh";

function shouldGoToRecall() {
    const proposalStore = useProposalStore();
    const hadSavedProposal = proposalStore.proposal.uuid;
    const exemptPages: Array<string> = [ROUTE_PATHS.Declined];

    return hadSavedProposal && !exemptPages.includes(window.location.pathname);
}

export function endSession() {
    const mainStore = useMainStore();
    mainStore.sessionExpiry.showTimeoutModal = false;
    mainStore.setDisplayStartDateIsInvalidModal(false);
    mainStore.setDisplayQuoteExpiredModal(false);
    mainStore.setDisplayQuoteAcceptedModal(false);

    if (window.location.pathname.split("?")[0] === ROUTE_PATHS.SessionExpired) return;

    // get the quote reference from the store
    const quoteReference = selectedQuote.value?.reference;

    axios.post(ApiEndpoints.SessionEnd).finally(() => {
        const route = shouldGoToRecall() ? ROUTE_PATHS.QuoteRecall : ROUTE_PATHS.SessionExpired;
        const query = quoteReference ? { reference: quoteReference } : { reference: "" };
        const queryString = Object.keys(query)
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
            .join("&");
        const fullUrl = `${window.location.origin}${route}${queryString ? `?${queryString}` : ""}`;
        window.sessionStorage.clear();

        // We don't use the Vue router here so as to avoid CSRF token mismatch issues
        window.location.href = fullUrl;
    });
}

let sessionTimeout = setTimeout(endSession, inactivityPeriodMilliseconds);
let warningTimeout = setTimeout(showTimeoutModal, warningPeriodMilliseconds);

function showTimeoutModal() {
    const mainStore = useMainStore();
    if (window.location.pathname !== ROUTE_PATHS.SessionExpired) {
        mainStore.sessionExpiry.showTimeoutModal = true;
    }
}

export function resetTimer() {
    clearTimeout(sessionTimeout);
    clearTimeout(warningTimeout);
    sessionTimeout = setTimeout(endSession, inactivityPeriodMilliseconds);
    warningTimeout = setTimeout(showTimeoutModal, warningPeriodMilliseconds);

    const timeSinceLastSessionRefresh = getSessionRefreshTime();

    if (timeSinceLastSessionRefresh === null) {
        return;
    }

    if (Date.now() - parseInt(timeSinceLastSessionRefresh) >= inactivityPeriodMilliseconds) {
        clearTimeout(sessionTimeout);
        return endSession();
    }

    axios
        .post(ApiEndpoints.SessionRefresh)
        .then(() => {
            saveSessionRefreshTime();
        })
        .catch(error => {
            if (error.response?.status === 419) {
                return endSession();
            }
        });
}

saveSessionRefreshTime();

function saveSessionRefreshTime() {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, Date.now().toString());
}

function getSessionRefreshTime() {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY);
}

window.addEventListener("mousemove", throttle(resetTimerIfPopupClosed, refreshPeriodMilliseconds));
window.addEventListener("touchmove", throttle(resetTimerIfPopupClosed, refreshPeriodMilliseconds));
window.addEventListener("keydown", throttle(resetTimerIfPopupClosed, refreshPeriodMilliseconds));

function resetTimerIfPopupClosed() {
    const mainStore = useMainStore();

    if (mainStore.sessionExpiry.showTimeoutModal) return;

    resetTimer();
}
